/**
 * Countdown picker: a ring counts the seconds down while names flash past,
 * slowing as it nears zero, and lands on the winner at 0. Purely cosmetic: the
 * winner is decided before `spin` is called and is always the last name shown.
 */
import { secureRandomInt } from '../lib/random.js';
import { animate, easeOutQuad } from './easing.js';

/** Whole seconds a countdown lasts for a slider duration (2–10 s in 0.5 s steps). */
export const countdownSeconds = (durationMs) => Math.max(2, Math.round(durationMs / 1000));

export function createCountdownView({ root, ring, number, name, user, onFrame = () => {} }) {
  // Mirrors the dial for the recap video (see recap/compositor.js).
  const frame = { mode: 'countdown', number: '', fraction: 1, name: '', user: '', running: false, landed: false };
  const publish = (patch) => onFrame(Object.assign(frame, patch));

  // The ring's circles use pathLength="100", so the dash offset is a percentage.
  const setRing = (fraction) => {
    ring.style.strokeDashoffset = String(100 * (1 - fraction));
    frame.fraction = fraction;
  };

  function setNumber(value) {
    number.textContent = String(value);
    number.classList.remove('pop');
    void number.offsetWidth; // restart the CSS animation
    number.classList.add('pop');
    frame.number = String(value);
  }

  function setName(title, subtitle) {
    name.textContent = title;
    user.textContent = subtitle;
    frame.name = title;
    frame.user = subtitle;
  }

  const showEntry = (entry) => setName(entry.displayName || entry.username, `@${entry.username}`);

  return {
    /** Idle display: the dial shows how many seconds a draw will count down. */
    showMessage(title, subtitle = '', seconds = '') {
      root.classList.remove('is-running', 'is-landed');
      setRing(1);
      number.classList.remove('pop');
      number.textContent = String(seconds);
      frame.number = String(seconds);
      setName(title, subtitle);
      publish({ running: false, landed: false });
    },

    /**
     * @param {{pool: object[], winner: object, duration: number, onTick?: () => void, onBeat?: (final: boolean) => void}} options
     */
    async spin({ pool, winner, duration, onTick, onBeat }) {
      const seconds = countdownSeconds(duration);
      // ~16 names/s at first, easing to a stop; the winner is always the last one.
      const count = Math.min(80, Math.max(16, seconds * 8));
      const names = [];
      for (let i = 0; i < count - 1; i++) {
        let pick = pool[secureRandomInt(pool.length)];
        if (pool.length > 1 && pick === names[i - 1]) pick = pool[(pool.indexOf(pick) + 1) % pool.length];
        names.push(pick);
      }
      // The name shown just before 0 must not already be the winner (same user, any entry).
      const sameUser = (entry) => (entry.key ?? entry.username) === (winner.key ?? winner.username);
      if (sameUser(names.at(-1))) names[names.length - 1] = pool.find((entry) => !sameUser(entry)) ?? winner;
      names.push(winner);

      root.classList.remove('is-landed');
      root.classList.add('is-running');
      let second = seconds;
      setNumber(second);
      onBeat?.(false);
      let shown = -1;

      await animate(seconds * 1000, (t) => {
        setRing(1 - t);
        const left = Math.max(1, Math.ceil(seconds * (1 - t)));
        if (t < 1 && left !== second) {
          second = left;
          setNumber(second);
          onBeat?.(false);
        }
        const index = Math.floor(easeOutQuad(t) * (names.length - 1));
        if (index !== shown) {
          shown = index;
          showEntry(names[index]);
          onTick?.();
        }
        publish({ running: true, landed: false });
      });

      showEntry(winner);
      setNumber(0);
      root.classList.remove('is-running');
      root.classList.add('is-landed');
      publish({ running: false, landed: true });
    },
  };
}
